import React, { useState } from 'react'

interface AddColorPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (colorData: {
    colorName: string
    materialTexture?: string
    aluminumTexture?: string
    thumbnail?: string
    modelPath?: string
    uploadedFiles?: {
      materialTexture?: File
      aluminumTexture?: File
      thumbnail?: File
      modelFile?: File
    }
  }) => Promise<void>
  fenceId: string
}

const AddColorPopup: React.FC<AddColorPopupProps> = ({ isOpen, onClose, onSave, fenceId }) => {
  const [colorName, setColorName] = useState('')
  const [materialTextureFile, setMaterialTextureFile] = useState<File | null>(null)
  const [aluminumTextureFile, setAluminumTextureFile] = useState<File | null>(null)
  
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleFileChange = (type: 'materialTexture' | 'aluminumTexture', file: File | null) => {
    switch (type) {
      case 'materialTexture':
        setMaterialTextureFile(file)
        break
      case 'aluminumTexture':
        setAluminumTextureFile(file)
        break
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!colorName.trim()) {
      setError('Nazwa koloru jest wymagana')
      return
    }

    setError(null)
    setUploading(true)

    try {
      // Wymagamy przynajmniej tekstury materiału
      if (!materialTextureFile) {
        setError('Tekstura materiału jest wymagana')
        setUploading(false)
        return
      }

      // Prześlij pliki
      const formData = new FormData()
      formData.append('fenceId', fenceId)
      formData.append('colorName', colorName.trim())
      
      if (materialTextureFile) {
        formData.append('materialTexture', materialTextureFile)
      }
      if (aluminumTextureFile) {
        formData.append('aluminumTexture', aluminumTextureFile)
      }

      const uploadResponse = await fetch('/api/admin/upload-texture', {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json()
        throw new Error(errorData.error || 'Błąd przesyłania plików')
      }

      const uploadData = await uploadResponse.json()
      const uploadedPaths = uploadData.files || {}

      // Przygotuj dane koloru
      const colorData = {
        colorName: colorName.trim(),
        materialTexture: uploadedPaths.materialTexture || '',
        aluminumTexture: uploadedPaths.aluminumTexture || '',
        thumbnail: uploadedPaths.thumbnail || '', // Miniatura jest generowana automatycznie
        modelPath: '', // Model DAE nie jest obsługiwany
        uploadedFiles: {
          materialTexture: materialTextureFile || undefined,
          aluminumTexture: aluminumTextureFile || undefined,
        }
      }

      await onSave(colorData)
      
      // Reset form
      setColorName('')
      setMaterialTextureFile(null)
      setAluminumTextureFile(null)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił błąd')
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    if (!uploading) {
      setColorName('')
      setMaterialTextureFile(null)
      setAluminumTextureFile(null)
      setError(null)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Dodaj nowy kolor</h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Color Name */}
          <div>
            <label htmlFor="colorName" className="block text-sm font-medium text-gray-700 mb-2">
              Nazwa koloru <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="colorName"
              value={colorName}
              onChange={(e) => setColorName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="np. A1 Złota Jesień"
              required
              disabled={uploading}
            />
          </div>

          {/* Material Texture */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tekstura materiału <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={(e) => handleFileChange('materialTexture', e.target.files?.[0] || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={uploading}
              required
            />
            {materialTextureFile && (
              <p className="text-sm text-green-600 mt-2">✓ Plik wybrany: {materialTextureFile.name}</p>
            )}
          </div>

          {/* Aluminum Texture */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tekstura aluminium
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={(e) => handleFileChange('aluminumTexture', e.target.files?.[0] || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={uploading}
            />
            {aluminumTextureFile && (
              <p className="text-sm text-green-600 mt-2">✓ Plik wybrany: {aluminumTextureFile.name}</p>
            )}
          </div>

          {/* Info about thumbnail */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Miniatura:</strong> Zostanie automatycznie wygenerowana z tekstury materiału po przesłaniu pliku.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={uploading || !colorName.trim()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {uploading ? 'Przesyłanie...' : 'Dodaj kolor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddColorPopup
